import { useState } from "react";
import { useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import Navbar from "@/components/navbar";
import { useAuth } from "@/lib/auth";
import {
  Shield,
  Users,
  Database,
  Building2,
  Calculator,
  Lightbulb,
  BarChart3,
  Settings,
  ArrowRight,
  FileUp,
  Sparkles,
  Activity,
  UserCog,
  Home,
  Target,
  ImagePlus
} from "lucide-react";

interface AdminSection {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  category: 'data' | 'settings' | 'insights';
  status?: 'active' | 'beta' | 'new';
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const [activeCategory, setActiveCategory] = useState<'all' | 'data' | 'settings' | 'insights'>('all');

  const adminSections: AdminSection[] = [
    // Data Management
    {
      id: 'import',
      title: 'Data Import',
      description: 'Import artists, venues, shows from CSV files and refresh social media data',
      icon: <FileUp className="h-5 w-5" />,
      path: '/admin/import',
      category: 'data',
      status: 'active'
    },
    {
      id: 'venues',
      title: 'Venue Management',
      description: 'Browse, search, and edit venue information including capacity and location',
      icon: <Building2 className="h-5 w-5" />,
      path: '/admin/venues',
      category: 'data',
      status: 'active'
    },
    {
      id: 'venue-images',
      title: 'Venue Image Review',
      description: 'Scan and fix venue images across all venues',
      icon: <ImagePlus className="h-5 w-5" />,
      path: '/admin/venue-images',
      category: 'data',
      status: 'new'
    },
    {
      id: 'dynamic-values',
      title: 'Dynamic Values',
      description: 'Review and approve dynamic filter values from your artist database',
      icon: <Sparkles className="h-5 w-5" />,
      path: '/admin/dynamic-values',
      category: 'data',
      status: 'new'
    },

    // Settings & Configuration
    {
      id: 'quick-personas',
      title: 'Quick Personas',
      description: 'Manage quick persona templates available to all users for instant targeting',
      icon: <Users className="h-5 w-5" />,
      path: '/admin/quick-personas',
      category: 'settings',
      status: 'new'
    },
    {
      id: 'calculator',
      title: 'Calculator Settings',
      description: 'Configure sponsorship calculator parameters and pricing models',
      icon: <Calculator className="h-5 w-5" />,
      path: '/admin/calculator',
      category: 'settings',
      status: 'beta'
    },
    {
      id: 'users',
      title: 'User Management',
      description: 'Manage admin users, roles, and permissions',
      icon: <UserCog className="h-5 w-5" />,
      path: '/admin/profile',
      category: 'settings',
      status: 'active'
    },

    // Insights & Planning
    {
      id: 'ideas',
      title: 'Ideas Log Sheet',
      description: 'Track feature requests, improvements, and development ideas',
      icon: <Lightbulb className="h-5 w-5" />,
      path: '/admin/ideas',
      category: 'insights',
      status: 'active'
    }
  ];

  const filteredSections = activeCategory === 'all' 
    ? adminSections 
    : adminSections.filter(section => section.category === activeCategory);

  const getStatusBadge = (status?: string) => {
    if (!status) return null;
    
    const variants: Record<string, { variant: any; label: string }> = {
      active: { variant: 'default' as const, label: 'Active' },
      beta: { variant: 'secondary' as const, label: 'Beta' },
      new: { variant: 'destructive' as const, label: 'New' }
    };
    
    const config = variants[status];
    if (!config) return null;
    
    return <Badge variant={config.variant} className="ml-2">{config.label}</Badge>;
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'data':
        return <Database className="h-4 w-4" />;
      case 'settings':
        return <Settings className="h-4 w-4" />;
      case 'insights':
        return <Activity className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'data':
        return 'Data Management';
      case 'settings':
        return 'Settings & Configuration';
      case 'insights':
        return 'Insights & Planning';
      default:
        return category;
    }
  };

  // Quick stats
  const stats = {
    dataItems: adminSections.filter(s => s.category === 'data').length,
    settingsItems: adminSections.filter(s => s.category === 'settings').length,
    insightsItems: adminSections.filter(s => s.category === 'insights').length,
    newItems: adminSections.filter(s => s.status === 'new').length,
  };

  if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-16 pt-24 text-center">
          <Shield className="h-16 w-16 text-neutral-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-neutral-900 mb-2">Admin Access Required</h1>
          <p className="text-neutral-600">You need administrator privileges to access this area.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8 pt-24">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold text-neutral-900">Admin Dashboard</h1>
          </div>
          <p className="text-neutral-600">
            Centralized management panel for MusicSponsor platform administration
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-neutral-600">Total Modules</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{adminSections.length}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-neutral-600">Data Management</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.dataItems}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-neutral-600">Settings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.settingsItems}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-neutral-600">New Features</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-destructive">{stats.newItems}</div>
            </CardContent>
          </Card>
        </div>

        {/* Category Filter */}
        <Tabs value={activeCategory} onValueChange={(value: any) => setActiveCategory(value)} className="mb-6">
          <TabsList className="grid w-full max-w-md grid-cols-4">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="data" className="flex items-center gap-1">
              <Database className="h-3 w-3" />
              Data
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-1">
              <Settings className="h-3 w-3" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="insights" className="flex items-center gap-1">
              <Activity className="h-3 w-3" />
              Insights
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Admin Sections Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSections.map((section) => (
            <Card 
              key={section.id} 
              className="hover:shadow-lg transition-shadow cursor-pointer group"
              onClick={() => setLocation(section.path)}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                      {section.icon}
                    </div>
                    <div>
                      <CardTitle className="text-lg flex items-center">
                        {section.title}
                        {getStatusBadge(section.status)}
                      </CardTitle>
                      <div className="flex items-center gap-1 mt-1">
                        {getCategoryIcon(section.category)}
                        <span className="text-xs text-neutral-500">
                          {getCategoryLabel(section.category)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-neutral-400 group-hover:text-primary transition-colors" />
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription>{section.description}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Admin Info */}
        <Card className="mt-8 bg-primary/5 border-primary/20">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Admin Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-neutral-600">Logged in as:</span>
                <span className="font-medium">{user.name}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-neutral-600">Role:</span>
                <Badge>{user.role === 'super_admin' ? 'Super Admin' : 'Admin'}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-neutral-600">Admin Level:</span>
                <span className="font-medium">{user.adminLevel || 'standard'}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}